/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn } from 'ahooks';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Modal,
  notification,
  TreeSelect,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  EyeOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteCategory } from 'services';
import type { CategoryQueryResult } from 'types';
import {
  generateExternalPathname,
  generateTableDataSource,
  generateTreeSelectData,
  localizeCategoryStatus,
  normalizeToString,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    CATEGORIES: { UPDATE_URL: CATEGORIES_UPDATE_URL },
    CATEGORY: { BASE_URL: CATEGORY_BASE_URL },
  },
} = PATHNAME;

type CategoriesTableProps = {
  loading: boolean;
  categories: CategoryQueryResult[];
  handleRefreshData: () => void;
};

const CategoriesTable = React.memo<CategoriesTableProps>(
  ({ loading, categories, handleRefreshData }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
    const navigate = useNavigate();

    const handleDeleteCategory = useLockFn(
      async ({ id }: CategoryQueryResult) => {
        deleteCategory({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'دسته‌بندی مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const categoriesDataSource = generateTableDataSource(categories, 'id');
    const categoriesTreeData = generateTreeSelectData(
      categories,
      'id',
      'title',
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 900, y: 700 }}
        columns={[
          {
            title: 'نام',
            dataIndex: 'title',
            key: 'title',
            width: '40%',
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <TreeSelect<string>
                  showSearch
                  className='w-80'
                  placeholder='دسته‌بندی مورد نظر را انتخاب کنید'
                  loading={loading}
                  treeData={categoriesTreeData}
                  filterTreeNode={(inputValue, treeNode) =>
                    normalizeToString(treeNode.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    navigate(
                      `${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${value}${DEFAULT_SLUG_SEPARATOR}`,
                    );
                  }}
                />
              </div>
            ),
            render: (_, { id, title }) => (
              <AntdRouterLink
                ellipsis
                to={`${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {title}
              </AntdRouterLink>
            ),
          },
          {
            title: 'پیوند یکتا',
            dataIndex: 'slug',
            key: 'slug',
            ellipsis: true,
            render: (_, { slug }) => <bdi>{slug}</bdi>,
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            ellipsis: true,
            render: (_, { status }) => {
              const { label, color } = localizeCategoryStatus(status);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'مرتبط با فیلد',
            dataIndex: 'relatedAttribute',
            key: 'relatedAttribute',
            width: 110,
            ellipsis: true,
            render: (_, { relatedAttribute }) => (
              <Flex justify='center'>
                {relatedAttribute ? (
                  <CheckCircleOutlinedIcon className='text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-red-500' />
                )}
              </Flex>
            ),
          },
          {
            title: 'نمایه‌گذاری',
            dataIndex: 'seoIndex',
            key: 'seoIndex',
            width: 100,
            ellipsis: true,
            render: (_, { seo }) => {
              const { isIndex } = seo ?? {};
              return (
                <Flex justify='center'>
                  {isIndex ? (
                    <CheckCircleOutlinedIcon className='text-green-500' />
                  ) : (
                    <CloseCircleOutlinedIcon className='text-red-500' />
                  )}
                </Flex>
              );
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id, slug } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'see-category',
                          label: (
                            <AntdRouterExternalLink
                              href={generateExternalPathname(
                                CATEGORY_BASE_URL,
                                slug,
                              )}
                              className='inline-block py-1 text-inherit'
                            >
                              <EyeOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                مشاهده در فروشگاه
                              </span>
                            </AntdRouterExternalLink>
                          ),
                        },
                        {
                          key: 'update-category',
                          label: (
                            <AntdRouterLink
                              to={`${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش دسته‌بندی‌
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-category',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف دسته‌بندی مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteCategory(
                                      record as CategoryQueryResult,
                                    );
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف دسته‌بندی‌</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={categoriesDataSource}
        pagination={false}
      />
    );
  },
);
CategoriesTable.displayName = 'CategoriesTable';

export { CategoriesTable };
